"use client"

import { NewTabLink } from "./new-tab-link"
import { ResourceLink } from "./resource-link"
import { useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { stepCallouts, stepItems, STEPS, type Step } from "@/lib/breach/steps"
import { extrasFor } from "@/lib/breach/step-extras"
import { buildStepPages, STEP_PAGE_TITLES } from "@/lib/breach/step-pages"
import { CALL_US, HELP_LINKS } from "@/lib/breach/help-links"
import { UpcisoCard } from "./upciso-card"
import { AssignForm } from "./assign-form"
import { WipeStatus } from "./wipe-status"
import { SaveProgress } from "./save-progress"
import { saveWorkspace } from "@/lib/breach/storage"
import { assignmentsForItem, closeLinkedAssignments } from "@/lib/breach/assignments"
import { directoriesForStep } from "@/lib/breach/directory/refs"
import { addLog, setOwner, toggleCheck, type Workspace } from "@/lib/breach/workspace"
import type { GoTo } from "./incident-nav"
import type { WorkspaceUpdater } from "./incident-tools"

const toneClass = {
  danger: "border-destructive/60 bg-destructive/10",
  warn: "border-yellow-500/50 bg-yellow-500/10",
  info: "border-border bg-card",
} as const

function addLogIf(w: Workspace, checked: boolean, text: string): Workspace {
  return checked ? addLog(w, `Done: ${text}`) : w
}

/**
 * Walks one step, one page at a time: Overview → Do this → Decisions →
 * Scripts → Wrap up (empty pages skipped). Position lives in the URL
 * (?v=step&step=<id>&p=<n>) so browser Back works.
 */
export function StepPager({ w, update, step, page, go }: {
  w: Workspace; update: WorkspaceUpdater; step: Step; page: number; go: GoTo
}) {
  const pages = useMemo(() => buildStepPages(step, w.intake), [step, w.intake])
  const p = Math.min(Math.max(page, 0), pages.length - 1)
  const kind = pages[p]

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [step.id, p])

  const stepIdx = STEPS.findIndex((s) => s.id === step.id)
  const nextStep = stepIdx >= 0 ? STEPS[stepIdx + 1] : undefined
  const isLastStep = stepIdx === STEPS.length - 1

  return (
    <div>
      <div className="mb-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm text-muted-foreground">
            Step {step.n} · {step.minutes} · Page {p + 1} of {pages.length}
          </p>
          <button type="button"
            className="rounded text-sm text-muted-foreground underline hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid print:hidden"
            onClick={() => go({ v: "index" })}>
            All steps
          </button>
        </div>
        <h1 className="mt-1 text-2xl font-bold sm:text-3xl">{step.title}</h1>
        <nav aria-label="Step pages" className="mt-3 print:hidden">
          <ol className="flex flex-wrap items-center gap-2">
            {pages.map((pk, i) => (
              <li key={pk}>
                <button type="button"
                  aria-current={i === p ? "step" : undefined}
                  aria-label={`Page ${i + 1} of ${pages.length}: ${STEP_PAGE_TITLES[pk]}`}
                  onClick={() => go({ v: "step", step: step.id, p: i })}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-solid",
                    i === p
                      ? "border-accent-solid bg-accent-solid text-accent-solid-foreground"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}>
                  {STEP_PAGE_TITLES[pk]}
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {kind === "overview" && <OverviewPage step={step} w={w} />}
      {kind === "do" && <DoPage step={step} w={w} update={update} go={go} />}
      {kind === "decisions" && <DecisionsPage step={step} w={w} />}
      {kind === "scripts" && <ScriptsPage step={step} w={w} />}
      {kind === "wrap-up" && (
        <WrapUpPage step={step} w={w} go={go} nextStep={nextStep} isLastStep={isLastStep} />
      )}

      <div className="mt-8 flex items-center justify-between gap-3 border-t border-border pt-4 print:hidden">
        <Button variant="outline"
          onClick={() => (p === 0 ? go({ v: "index" }) : go({ v: "step", step: step.id, p: p - 1 }))}>
          <ArrowLeft /> {p === 0 ? "Back to all steps" : "Back"}
        </Button>
        {p < pages.length - 1 && (
          <Button className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90"
            onClick={() => go({ v: "step", step: step.id, p: p + 1 })}>
            Next <ArrowRight />
          </Button>
        )}
      </div>
      <SaveProgress className="mt-3" save={() => saveWorkspace(w)} savedAt={w.updatedAt} />
    </div>
  )
}

function OverviewPage({ step, w }: { step: Step; w: Workspace }) {
  const extras = extrasFor(step.id, w.intake)
  return (
    <section className="grid gap-4" aria-label="Overview">
      <h2 className="text-xl font-semibold">Overview</h2>
      <p className="text-muted-foreground">{step.summary}</p>
      {stepCallouts(step, w.intake).map((c, k) => (
        <p key={k} className={cn("rounded-md border p-3 text-sm", toneClass[c.tone])}>{c.text}</p>
      ))}
      {extras && (
        <div className="grid gap-4 sm:grid-cols-2">
          {extras.outputs.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-1 text-sm font-semibold">You&apos;ll have</h3>
              <ul className="list-disc pl-5 text-sm text-muted-foreground">
                {extras.outputs.map((o) => <li key={o}>{o}</li>)}
              </ul>
            </div>
          )}
          {extras.people.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-1 text-sm font-semibold">Who to involve</h3>
              <p className="text-sm text-muted-foreground">{extras.people.join(" · ")}</p>
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Based on NIST SP 800-61 Rev. 3 · {step.nist.join(", ")} · <NewTabLink href="/sources" className="underline">sources</NewTabLink>
      </p>
    </section>
  )
}

function DoPage({ step, w, update, go }: { step: Step; w: Workspace; update: WorkspaceUpdater; go: GoTo }) {
  const items = stepItems(step, w.intake)
  return (
    <section className="grid gap-4" aria-label="Do this">
      <h2 className="text-xl font-semibold">Do this</h2>
      <ul className="grid gap-3">
        {items.map((it) => {
          const c = w.checks[it.id]
          return (
            <li key={it.id} className="flex items-start gap-3 rounded-lg border border-border bg-card p-3">
              <input id={it.id} type="checkbox" className="mt-1 h-5 w-5 shrink-0" checked={!!c?.done}
                onChange={(e) => update((x) => addLogIf(e.target.checked ? closeLinkedAssignments(toggleCheck(x, it.id, true), it.id) : toggleCheck(x, it.id, false), e.target.checked, it.text))} />
              <div className="grid flex-1 gap-1">
                <label htmlFor={it.id} className={cn("text-base", c?.done && "text-muted-foreground line-through")}>
                  {it.urgent && <span className="mr-2 rounded bg-destructive/20 px-1.5 py-0.5 text-xs font-semibold uppercase text-destructive">Now</span>}
                  {it.text}
                </label>
                {it.link && (it.link.href === "/harden"
                  ? <button type="button" className="w-fit text-sm font-medium underline print:hidden" onClick={() => go({ v: "harden", p: 0 })}>{it.link.label}</button>
                  : <ResourceLink href={it.link.href} className="w-fit text-left text-sm font-medium underline print:hidden">{it.link.label}</ResourceLink>)}
                {it.tool && <button type="button" className="w-fit text-sm font-medium underline print:hidden" onClick={() => go({ v: it.tool!.view })}>{it.tool.label}</button>}
                {it.why && <p className="text-xs text-muted-foreground">{it.why}</p>}
                {it.ask === "wipe" && <WipeStatus itemId={it.id} w={w} update={update} />}
                <Delegate itemId={it.id} text={it.text} w={w} update={update} />
                <input aria-label={`Owner for: ${it.text}`} placeholder="Who's doing this?" value={c?.owner ?? ""}
                  onChange={(e) => update((x) => setOwner(x, it.id, e.target.value))}
                  className="w-48 rounded border border-border bg-background px-2 py-1 text-xs print:hidden" />
              </div>
            </li>
          )
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        You don&apos;t have to finish this list before moving on — the checkboxes are saved as you go.
      </p>
    </section>
  )
}

function DecisionsPage({ step, w }: { step: Step; w: Workspace }) {
  const extras = extrasFor(step.id, w.intake)
  if (!extras) return null
  return (
    <section className="grid gap-4" aria-label="Decisions">
      <h2 className="text-xl font-semibold">Decisions</h2>
      <ul className="grid gap-2">
        {extras.decisions.map((d) => (
          <li key={d.q} className="rounded-lg border border-border bg-card p-4 text-sm">
            <strong>{d.q}</strong>
            <p className="mt-1 text-muted-foreground">{d.guidance}</p>
          </li>
        ))}
      </ul>
    </section>
  )
}

function ScriptsPage({ step, w }: { step: Step; w: Workspace }) {
  const extras = extrasFor(step.id, w.intake)
  if (!extras) return null
  return (
    <section className="grid gap-4" aria-label="Scripts and templates">
      <h2 className="text-xl font-semibold">Scripts and templates</h2>
      <div className="grid gap-2">
        {extras.scripts.map((sc) => (
          <ScriptDetails key={sc.title} title={sc.title} usage={sc.use} text={sc.text} />
        ))}
      </div>
    </section>
  )
}

function ScriptDetails({ title, usage, text }: { title: string; usage: string; text: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard unavailable */ }
  }
  return (
    <details className="rounded-lg border border-border bg-card p-3 text-sm">
      <summary className="cursor-pointer font-medium">{title}</summary>
      <p className="mt-2 text-xs text-muted-foreground">{usage} Fill in the [BRACKETS]. Have counsel review anything you send outside the company.</p>
      <pre className="mt-2 whitespace-pre-wrap rounded bg-background p-3 text-xs">{text}</pre>
      <Button size="sm" variant="outline" className="mt-2 print:hidden" onClick={copy}>
        {copied ? "Copied" : "Copy"}
      </Button>
    </details>
  )
}

function WrapUpPage({ step, w, go, nextStep, isLastStep }: {
  step: Step; w: Workspace; go: GoTo; nextStep: Step | undefined; isLastStep: boolean
}) {
  const extras = extrasFor(step.id, w.intake)
  const help = HELP_LINKS[step.id] ?? []
  return (
    <section className="grid gap-4" aria-label="Wrap up">
      <h2 className="text-xl font-semibold">Wrap up</h2>
      {extras && (
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-1 text-sm font-semibold">This step is done when</h3>
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            {extras.doneWhen.map((o) => <li key={o}>{o}</li>)}
          </ul>
        </div>
      )}
      {extras && extras.resources.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-1 text-sm font-semibold">Read more</h3>
          <ul className="grid gap-1 text-sm">
            {extras.resources.map((r) => (
              <li key={r.url}>
                <a href={r.url} target="_blank" rel="noopener" className="underline">{r.label}</a>
                {r.note && <span className="text-muted-foreground"> — {r.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {directoriesForStep(step.id).length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4 print:hidden">
          <h3 className="mb-2 text-sm font-semibold">Helpful directories for this step</h3>
          <ul className="grid gap-2 sm:grid-cols-2">
            {directoriesForStep(step.id).map((d) => (
              <li key={d.id} className="rounded-md border border-border p-3 text-sm">
                <ResourceLink href={d.href} className="text-left font-medium underline">{d.title}</ResourceLink>
                <p className="mt-1 text-xs text-muted-foreground">{d.blurb}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {extras && (
        <div className="rounded-lg border border-accent/50 bg-accent/10 p-4 text-sm print:hidden">
          <h3 className="mb-1 text-sm font-semibold">Need help with &ldquo;{step.title}&rdquo;?</h3>
          <ul className="list-disc pl-5 text-muted-foreground">
            {extras.callWhen.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <p className="mt-2">
            <NewTabLink href={CALL_US.href} className="font-semibold underline">{CALL_US.label}</NewTabLink>
            {CALL_US.phone && <> · <a className="underline" href={`tel:${CALL_US.phone}`}>{CALL_US.phone}</a></>}
            <span className="text-muted-foreground"> (InventiveHQ, a paid service; the guide works without it)</span>
          </p>
          {help.length > 0 && (
            <div className="mt-3 rounded-md border border-border p-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Help with this step · InventiveHQ</p>
              {help.map((h) => (
                <p key={h.url}>
                  <a className="font-medium underline" href={h.url} rel="sponsored noopener" target="_blank">{h.label}</a> — {h.blurb}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
      {step.id === "learn" && <UpcisoCard heading="When you're ready to shore up your security" />}
      <div className="mt-2 flex flex-wrap gap-3 print:hidden">
        <Button variant="outline" onClick={() => go({ v: "index" })}>Back to all steps</Button>
        {isLastStep || !nextStep ? (
          <Button className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90"
            onClick={() => go({ v: "harden", p: 0 })}>
            Next: make it harder next time <ArrowRight />
          </Button>
        ) : (
          <Button className="bg-accent-solid text-accent-solid-foreground hover:bg-accent-solid/90"
            onClick={() => go({ v: "step", step: nextStep.id, p: 0 })}>
            Next step: {nextStep.title} <ArrowRight />
          </Button>
        )}
      </div>
    </section>
  )
}

const shortTitle = (t: string) => (t.length > 90 ? t.slice(0, 87).trimEnd() + "…" : t)

/** "Assign to someone" for any checklist item, and the status of anything already handed out. */
function Delegate({ itemId, text, w, update }: { itemId: string; text: string; w: Workspace; update: WorkspaceUpdater }) {
  const [open, setOpen] = useState(false)
  const linked = assignmentsForItem(w, itemId)
  return (
    <div className="grid gap-1 print:hidden">
      {linked.map((a) => (
        <p key={a.id} className="text-xs text-muted-foreground">
          Assigned to <strong className="text-foreground">{a.assigneeName}</strong> · {a.status === "done" ? "done" : "waiting on them"}
        </p>
      ))}
      {open
        ? <AssignForm w={w} update={update} defaults={{ title: shortTitle(text), itemId }} onClose={() => setOpen(false)} />
        : <button type="button" className="w-fit text-xs underline text-muted-foreground hover:text-foreground" onClick={() => setOpen(true)}>Assign to someone</button>}
    </div>
  )
}
